// VRG-691 — react-query hooks for GTM → Sales: the deals board, leads and prospect companies.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { boardLane, type MasterPipelineFilters, type SalesStage, type SourceChannel, type StageProbabilities } from '@repo/types'

import {
  gtmApi,
  salesFlowQuery,
  type SalesAccountInput,
  type SalesBoard,
  type SalesCreateDealInput,
  type SalesDealInput,
  type SalesLeadInput,
} from 'services'

const ROOT = ['internal', 'gtm', 'sales'] as const
export const SALES_BOARD_KEY = [...ROOT, 'board'] as const

const boardKey = (range: { since?: string; until?: string } | null) => [...SALES_BOARD_KEY, range?.since ?? 'all', range?.until ?? 'all']

export const useSalesBoard = (range: { since?: string; until?: string } | null) =>
  useQuery({
    queryKey: boardKey(range),
    queryFn: () => gtmApi.salesBoard(range),
    staleTime: 30 * 1000,
    retry: false,
  })

/**
 * VRG-696 — the ONE flow query, shared by GTM → Channels (master pipeline, every filter) and
 * Sales → Pipeline (flow chart + by-source table, date range only). Under ROOT, so every Sales
 * write that invalidates the board invalidates this too. Keyed by the serialised filters.
 */
export const useSalesFlow = (filters: MasterPipelineFilters) =>
  useQuery({
    queryKey: [...ROOT, 'flow', salesFlowQuery(filters)],
    queryFn: () => gtmApi.salesFlow(filters),
    staleTime: 30 * 1000,
    retry: false,
    // Keep the chart on screen while a new filter loads (no skeleton flash on every change).
    placeholderData: (prev) => prev,
  })

/** VRG-700 — the automatic-Won dry run. Under ROOT, so a Sales write refreshes it too. */
export const useSalesAutoWonPreview = () =>
  useQuery({ queryKey: [...ROOT, 'auto-won-preview'], queryFn: () => gtmApi.salesAutoWonPreview(), staleTime: 60 * 1000, retry: false })

export const useSalesAccounts = () =>
  useQuery({ queryKey: [...ROOT, 'accounts'], queryFn: () => gtmApi.salesAccounts(), staleTime: 30 * 1000, retry: false })

export const useSalesAccount = (id: string | null) =>
  useQuery({
    queryKey: [...ROOT, 'account', id],
    queryFn: () => gtmApi.salesAccount(id!),
    enabled: !!id,
    staleTime: 10 * 1000,
    retry: false,
  })

export const useSalesLeads = () =>
  useQuery({ queryKey: [...ROOT, 'leads'], queryFn: () => gtmApi.salesLeads(), staleTime: 30 * 1000, retry: false })

/** Every Sales read moves together after a write: one record feeds the board, tiles and lists. */
const useInvalidateSales = () => {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ROOT })
}

export const useCreateSalesAccount = () => {
  const invalidate = useInvalidateSales()
  return useMutation({ mutationFn: (input: SalesAccountInput) => gtmApi.createSalesAccount(input), onSuccess: invalidate })
}

export const useUpdateSalesAccount = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesAccountInput }) => gtmApi.updateSalesAccount(id, input),
    onSuccess: invalidate,
  })
}

export const useCreateSalesLead = () => {
  const invalidate = useInvalidateSales()
  return useMutation({ mutationFn: (input: SalesLeadInput) => gtmApi.createSalesLead(input), onSuccess: invalidate })
}

export const useUpdateSalesLead = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesLeadInput }) => gtmApi.updateSalesLead(id, input),
    onSuccess: invalidate,
  })
}

export const useDeleteSalesLead = () => {
  const invalidate = useInvalidateSales()
  return useMutation({ mutationFn: (id: string) => gtmApi.deleteSalesLead(id), onSuccess: invalidate })
}

/** VRG-701 — one channel's sub-source picker options (its list + its saved list). */
export const useSalesSubSources = (channel: SourceChannel | null) =>
  useQuery({
    queryKey: [...ROOT, 'sub-sources', channel],
    queryFn: () => gtmApi.salesSubSources(channel!),
    enabled: !!channel,
    staleTime: 60 * 1000,
    retry: false,
  })

/** VRG-701 — "Add sub-source". Refreshes that channel's picker. */
export const useAddSalesSubSource = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channel, label }: { channel: SourceChannel; label: string }) => gtmApi.addSalesSubSource(channel, label),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: [...ROOT, 'sub-sources', v.channel] }),
  })
}

/** VRG-701 — Add deal. The result carries `warnings` (a second open deal is warned about, not refused). */
export const useCreateSalesDeal = () => {
  const invalidate = useInvalidateSales()
  return useMutation({ mutationFn: (input: SalesCreateDealInput) => gtmApi.createSalesDeal(input), onSuccess: invalidate })
}

export const useUpdateSalesDeal = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SalesDealInput }) => gtmApi.updateSalesDeal(id, input),
    onSuccess: invalidate,
  })
}

export const useCloseSalesDeal = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: ({ id, reason, note }: { id: string; reason: 'lost' | 'archived'; note?: string | null }) =>
      gtmApi.closeSalesDeal(id, reason, note),
    onSuccess: invalidate,
  })
}

export const useReopenSalesDeal = () => {
  const invalidate = useInvalidateSales()
  return useMutation({ mutationFn: (id: string) => gtmApi.reopenSalesDeal(id), onSuccess: invalidate })
}

/** VRG-736 — the tag unit cost and markup (Sales → Tag economics). */
export const useUpdateSalesTagEconomics = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: (input: { unit_cost_usd?: number | null; markup?: number | null }) => gtmApi.updateSalesTagEconomics(input),
    onSuccess: invalidate,
  })
}

export const useUpdateSalesSettings = () => {
  const invalidate = useInvalidateSales()
  return useMutation({
    mutationFn: (p: StageProbabilities) => gtmApi.updateSalesSettings(p),
    onSuccess: invalidate,
  })
}

/**
 * A drag on the board. OPTIMISTIC: the card moves in every cached board at once, and if the
 * server refuses (evidence, a missing confirmation, a network error) every cache is put back
 * exactly as it was. The tiles and chart are refetched on settle, because they are the
 * server's folds and are not recomputed here.
 */
export const useMoveSalesDeal = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, toStage, confirm, reopen }: { id: string; toStage: SalesStage; confirm?: boolean; reopen?: boolean }) =>
      gtmApi.moveSalesDeal(id, toStage, confirm ?? false, reopen ?? false),
    onMutate: async ({ id, toStage, reopen }) => {
      await qc.cancelQueries({ queryKey: SALES_BOARD_KEY })
      const previous = qc.getQueriesData<SalesBoard>({ queryKey: SALES_BOARD_KEY })
      for (const [key, board] of previous) {
        if (!board) continue
        qc.setQueryData<SalesBoard>(key, {
          ...board,
          deals: board.deals.map((d) => {
            if (d.id !== id) return d
            const moved = reopen
              ? { ...d, stage: toStage, days_in_stage: 0, status: 'open' as const, closed_reason: null }
              : { ...d, stage: toStage, days_in_stage: 0 }
            return { ...moved, lane: boardLane(moved) }
          }),
        })
      }
      return { previous }
    },
    onError: (_error, _vars, context) => {
      for (const [key, board] of context?.previous ?? []) qc.setQueryData(key, board)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ROOT }),
  })
}
